package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.CreateQaIgnoreRequest
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSummaryDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.i18n.t
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource

@Composable
internal fun ProjectQaSurface(
    slug: String,
    project: ProjectDetailDto,
    authority: ProjectAuthority,
    authoritySubject: ProjectAuthoritySubject,
    mutationClaim: ProjectActionClaim?,
    onOpenIssue: (QaIssueDto) -> Unit,
    rulesAction: @Composable () -> Unit,
    /**
     * The locale this page was opened *about*, from `?locale=`.
     *
     * The readiness band's "12 QA errors" is a per-language number, and following it used to land
     * on every language at once — leaving the release manager to re-apply a filter the system had
     * already computed before they clicked.
     */
    initialLocale: String = "",
    onOpenProject: (() -> Unit)? = null,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    /**
     * The issues **and the totals around them**.
     *
     * `GET /qa?counts=true` answers with a [QaSummaryDto] whose counts are taken before the
     * thousand-issue cap, so a group header can state the check's real size and the page can say
     * out loud when the list it drew is a page rather than the set. The bare list route cannot do
     * either: it returns a truncated array and nothing that says it was truncated.
     */
    loadIssues: suspend (String, String?) -> QaSummaryDto = { projectSlug, scope ->
        ApiClient.qaSummary(projectSlug, scope)
    },
    addIgnore: suspend (String, CreateQaIgnoreRequest) -> Unit = { projectSlug, request ->
        ApiClient.addQaIgnore(projectSlug, request)
    },
) {
    // Loading is driven by `loadReloadable` rather than a hand-rolled LaunchedEffect + poll loop: the
    // previous version restarted its effect on every recomposition, so it re-fetched forever and
    // never left the loading state.
    var retryKey by remember(authoritySubject) { mutableStateOf(0) }
    // **The locale filter is a server-side scope, not a client-side one.** The page fetched every
    // target locale's issues and then hid all but one, so picking German still ran the QA engine
    // over the other seven languages first.
    var locale by remember(authoritySubject, initialLocale) { mutableStateOf(initialLocale) }
    val reloadable = loadReloadable(authoritySubject, locale, reloadToken = retryKey) {
        requestProjectResource(
            key = authoritySubject.action,
            expectedSlug = slug,
            memberships = currentMemberships(),
            currentRoute = currentRoute,
            currentMemberships = currentMemberships,
        ) { route ->
            loadIssues(route.project.slug, locale.takeIf { it.isNotBlank() })
        }
    }

    Column(QaPageStyle.toModifier()) {
        when (val snapshot = reloadable.state) {
            Async.Loading -> QaLoadingState()
            is Async.Failure -> {
                PageHeader(
                    title = t("editor.project_qa_surface.quality_assurance", "Quality assurance"),
                    subtitle = t("editor.project_qa_surface.find_and_fix_translation_risks_before", "Find and fix translation risks before they ship."),
                )
                // A translator who followed a shared link, or a project that has since been
                // deleted, is a boundary rather than a fault — `loadReloadable` already carries the
                // status here, and this was the one editor surface throwing it away at the render.
                AsyncFailureState(
                    failure = snapshot,
                    subject = t("editor.project_qa_surface.quality_assurance_2", "quality assurance"),
                    onRetry = { retryKey++ },
                )
            }
            is Async.Success -> QaContent(
                project = project,
                issues = snapshot.value.issues,
                summary = snapshot.value,
                authority = authority,
                actionStateKey = authoritySubject,
                mutationClaim = mutationClaim,
                refreshing = reloadable.refreshing,
                refreshError = reloadable.refreshError?.message,
                onRetry = { retryKey++ },
                onOpenIssue = onOpenIssue,
                locale = locale,
                onLocaleChange = { locale = it },
                onOpenProject = onOpenProject,
                currentRoute = currentRoute,
                currentMemberships = currentMemberships,
                addIgnore = addIgnore,
                rulesAction = rulesAction,
            )
        }
    }
}
